import React, { FC, memo, useCallback, ReactNode } from 'react';
import { Box } from 'entities/Box';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector, useAppDispatch } from 'app/config/storeConfig/hooks';
import { SelectOfficeContent } from 'widgets/SelectBoxSizeForm/ui/SelectOfficeContent';
import { ModernSelectBoxSizeContent } from './ModernSelectBoxSizeContent';
import { ModernSelectBoxSizeContentMobile } from './ModernSelectBoxSizeContentMobile';
import { WarehouseForBooking } from 'entities/Warehouse';
import { getSelectedUnitTypeFilter } from 'features/UnitTypeFilter';
import { UnitType } from 'entities/Contract';
import { Button } from 'shared/ui/Button';
import { SizeGuideModal, sizeGuideModalActions } from 'features/SizeGuideModal';
import { useGetSizeCodesForBookingQuery } from 'entities/SizeCode';
import { useGetRentOptionsQuery } from 'entities/RentOption';
import { SelectSizeAndBoxSection } from './SelectSizeAndBoxSection';
import { ModernWarehouseInfo } from 'widgets/ModernWarehouseInfo';
import { WarehouseSizesCard } from 'widgets/WarehouseSizesCard';
import { RentOptionsList } from 'widgets/RentOptionCard';
import { WarehouseImageAndMap } from 'widgets/WarehouseImageAndMap';
import { MobileBookingInfoStack } from 'widgets/MobileBookingInfoStack';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';

interface ModernSelectBoxSizeFormProps {
  selectedBoxId: string | undefined;
  selectedWarehouseId: string | undefined;
  warehouseInfo: Nullable<WarehouseForBooking>;
  onSelectBox: (box: Box, sizeCodeId?: string) => void;
  onCompleteStep: (box: Box, sizeCodeId?: string) => void;
  onSelectWarehouse: (warehouse: WarehouseForBooking) => void;
}

export const ModernSelectBoxSizeForm: FC<ModernSelectBoxSizeFormProps> = memo((props) => {
  const { selectedWarehouseId, warehouseInfo, selectedBoxId, onSelectBox, onCompleteStep } = props;
  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();
  const { isDesktop } = useScreenBreakpoints();
  const selectedUnitTypeFilter = useAppSelector(getSelectedUnitTypeFilter);

  const { data: sizeCodes } = useGetSizeCodesForBookingQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });
  const { data: rentOptions } = useGetRentOptionsQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });

  const openSizeGuideModal = useCallback((): void => {
    dispatch(sizeGuideModalActions.setOpenModal(true));
  }, [dispatch]);

  const stepTitle = selectedUnitTypeFilter === UnitType.BOX ? t('Select the size of the box') : t('Select office');
  const sizeGuideButton = (
    <Button theme="clear" fontSize="large" onClick={openSizeGuideModal} className="text-green-500 hover:text-green-600 font-medium">
      <span className="underline">{t('Size Guide')}</span>
    </Button>
  );

  // Handle box selection from WarehouseSizesCard
  const handleSelectBoxFromCard = useCallback(
    (box: Box, sizeCodeId: string): void => {
      onSelectBox(box, sizeCodeId);
    },
    [onSelectBox],
  );

  // Handle box booking from WarehouseSizesCard
  const handleBookBoxFromCard = useCallback(
    (box: Box, sizeCodeId: string): void => {
      onCompleteStep(box, sizeCodeId);
    },
    [onCompleteStep],
  );

  const renderUnits = (unitType: UnitType): ReactNode => {
    if (unitType === UnitType.BOX) {
      if (isDesktop) {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <ModernSelectBoxSizeContent {...props} />;
      } else {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <ModernSelectBoxSizeContentMobile {...props} />;
      }
    }

    if (unitType === UnitType.OFFICE) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <SelectOfficeContent {...props} />;
    }

    return null;
  };

  // Mobile layout - single column
  if (!isDesktop) {
    return (
      <div className="space-y-1 pb-8">
        {/* Mobile warehouse info stack with overlapping layout */}
        {selectedWarehouseId && warehouseInfo && <MobileBookingInfoStack warehouseInfo={warehouseInfo} />}

        <SelectSizeAndBoxSection>
          <div id="select-box-size-section" className="space-y-6 px-3 pt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-primary font-tektur">{stepTitle}</h2>
              {/* {sizeGuideButton} */}
            </div>
          </div>

          <div className="w-full px-3 py-6">{renderUnits(selectedUnitTypeFilter)}</div>
        </SelectSizeAndBoxSection>

        <SizeGuideModal sizeCodes={sizeCodes} />
      </div>
    );
  }

  // Desktop layout - two column
  return (
    <div className="space-y-6">
      {/* Full width warehouse image and map at the top */}
      {selectedWarehouseId && warehouseInfo && (
        <SelectSizeAndBoxSection>
          <WarehouseImageAndMap warehouseInfo={warehouseInfo} />
        </SelectSizeAndBoxSection>
      )}

      {/* Two column layout below for select box + rent options */}
      <div className="flex gap-6">
        <div className="flex-1 flex flex-col gap-6">
          {selectedWarehouseId && warehouseInfo && (
            <SelectSizeAndBoxSection>
              <ModernWarehouseInfo warehouseInfo={warehouseInfo} />
            </SelectSizeAndBoxSection>
          )}

          <SelectSizeAndBoxSection>
            <div id="select-box-size-section" className="space-y-6 px-3 pt-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-primary font-tektur">{stepTitle}</h2>
                {/* {sizeGuideButton} */}
              </div>
            </div>

            <div className="w-full px-3 py-6">{renderUnits(selectedUnitTypeFilter)}</div>
          </SelectSizeAndBoxSection>
        </div>

        <div className="flex flex-col gap-6">
          {warehouseInfo && rentOptions && (
            <SelectSizeAndBoxSection className="w-[350px]">
              <WarehouseSizesCard
                warehouse={warehouseInfo}
                rentOptions={rentOptions}
                selectedBoxId={selectedBoxId}
                onSelectBox={handleSelectBoxFromCard}
                onBookBox={handleBookBoxFromCard}
              />
            </SelectSizeAndBoxSection>
          )}

          {warehouseInfo && rentOptions && (
            <div className="w-[350px] flex-shrink-0 space-y-6">
              <RentOptionsList selectedWarehouseId={selectedWarehouseId} />
            </div>
          )}
        </div>
      </div>

      <SizeGuideModal sizeCodes={sizeCodes} />
    </div>
  );
});
